import React from 'react';
import { useI18n } from '../../context/I18nContext';

const ToggleRow = ({ title, description, checked, onChange }) => (
  <label className="flex items-center justify-between gap-4 p-4 rounded-lg border border-gray-200 cursor-pointer">
    <div>
      <span className="block text-sm font-medium text-gray-800 mb-1">{title}</span>
      <span className="block text-xs text-gray-600">{description}</span>
    </div>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only"
    />
    <span className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </span>
  </label>
);

const GeneralSettingsSidebar = ({
  settings,
  setSettings,
  currentCable,
  setCurrentCable,
  cables,
  setCables,
  setFileName,
  setRevision,
  setSchemaElettrico,
  setInstallazione
}) => {
  const { t } = useI18n();

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t('settings.projectSectionTitle')}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{t('settings.projectSectionDesc')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.fileName')}</label>
          <input
            type="text"
            value={settings.fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder={t('settings.placeholderFileName')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.revision')}</label>
          <input
            type="number"
            value={settings.revision}
            onChange={(e) => setRevision(Math.max(0, Math.min(99, parseInt(e.target.value, 10) || 0)))}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            min="0"
            max="99"
            placeholder={t('settings.placeholderRevision')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.electricalSchema')}</label>
          <input
            type="text"
            value={settings.schemaElettrico}
            onChange={(e) => setSchemaElettrico(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder={t('settings.placeholderSchema')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.installation')}</label>
          <input
            type="text"
            value={settings.installazione}
            onChange={(e) => setInstallazione(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder={t('settings.placeholderInstallation')}
          />
        </div>
        </div>
      </section>

      <section className="space-y-4 pt-4 border-t border-gray-200">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t('settings.orderSectionTitle')}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{t('settings.orderSectionDesc')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.orderReference')}</label>
          <input
            type="text"
            value={settings.commessaId || ''}
            onChange={(e) => setSettings((prev) => ({ ...prev, commessaId: e.target.value }))}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder={t('settings.placeholderOrderReference')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.orderDate')}</label>
          <input
            type="date"
            value={settings.commessaDate || ''}
            onChange={(e) => setSettings((prev) => ({ ...prev, commessaDate: e.target.value }))}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.quantityWiring')}</label>
          <input
            type="number"
            value={settings.quantitaCablaggi}
            onChange={(e) => setSettings((prev) => ({ ...prev, quantitaCablaggi: parseInt(e.target.value, 10) || 1 }))}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            min="1"
          />
        </div>
        </div>
      </section>

      <section className="space-y-4 pt-4 border-t border-gray-200">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t('settings.standardSectionTitle')}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.sectionStandard')}</label>
          <select
            value={settings.useAWG ? 'awg' : 'metric'}
            onChange={(e) => {
              const newUseAWG = e.target.value === 'awg';
              setSettings((prev) => ({ ...prev, useAWG: newUseAWG }));
            }}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value="metric">{t('settings.metric')}</option>
            <option value="awg">{t('settings.american')}</option>
          </select>
        </div>
        </div>
      </section>

      <section className="space-y-4 pt-4 border-t border-gray-200">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t('settings.markingConfiguration')}
          </h3>
        </div>

        <ToggleRow
          title={t('settings.markCables')}
          description={t('settings.markCablesDesc')}
          checked={settings.marcaCavi === 1}
          onChange={(checked) => setSettings((prev) => ({ ...prev, marcaCavi: checked ? 1 : 0 }))}
        />
        <ToggleRow
          title={t('settings.markingModePerConnection')}
          description={t('settings.markingModePerConnectionDesc')}
          checked={!!settings.marcaturePerCollegamento}
          onChange={(checked) => setSettings((prev) => ({ ...prev, marcaturePerCollegamento: checked }))}
        />
        {settings.marcaturePerCollegamento && (
          <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
            <strong>{t('settings.modeActive')}</strong> {t('settings.modeActiveDesc')}
          </p>
        )}
      </section>

    </div>
  );
};

export default GeneralSettingsSidebar;
